import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ShieldAlert, Star, Trash2, Plus, Phone, Mail, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Emergency Contacts settings panel.
 *
 * Sits on the provider Security tab. Lets the provider add up to 5
 * contacts who will be SMS-fanned on a missed check-in or a panic
 * alert. Primary contact rendered first and gets a gold star pill.
 */
export default function EmergencyContactsCard() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', relationship: '', is_primary: false });
  const [saving, setSaving] = useState(false);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/safety/contacts');
      setContacts(res.data || []);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleAdd = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Name and phone are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/safety/contacts', form);
      toast.success('Contact added');
      setForm({ name: '', phone: '', email: '', relationship: '', is_primary: false });
      setAdding(false);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this emergency contact?')) return;
    try {
      await api.delete(`/safety/contacts/${id}`);
      toast.success('Contact removed');
      fetchContacts();
    } catch {
      toast.error('Could not remove contact');
    }
  };

  const handleMakePrimary = async (id) => {
    try {
      await api.put(`/safety/contacts/${id}`, { is_primary: true });
      toast.success('Primary contact updated');
      fetchContacts();
    } catch {
      toast.error('Could not update');
    }
  };

  return (
    <div className="card-surface p-6" data-testid="emergency-contacts-card">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-[#D4AF37]" />
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Emergency contacts</h3>
        </div>
        {!adding && contacts.length < 5 && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAdding(true)}
            className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-amber-50 gap-1"
            data-testid="add-emergency-contact-btn"
          >
            <Plus className="h-3.5 w-3.5" />Add contact
          </Button>
        )}
      </div>
      <p className="text-xs text-stone-500 mb-4">
        Up to 5 trusted people. They&rsquo;ll be texted automatically if you miss a check-in on a mobile booking, or if you trigger a panic alert.
      </p>

      {loading ? (
        <p className="text-sm text-stone-400">Loading&hellip;</p>
      ) : (
        <>
          {contacts.length === 0 && !adding && (
            <div className="text-center py-6 border border-dashed border-[#E6E4DD] rounded-lg">
              <p className="text-sm text-stone-500 mb-3">No emergency contacts yet</p>
              <Button
                size="sm"
                onClick={() => setAdding(true)}
                className="btn-primary"
                data-testid="add-first-contact-btn"
              >
                Add your first contact
              </Button>
            </div>
          )}

          {contacts.length > 0 && (
            <ul className="space-y-2" data-testid="emergency-contacts-list">
              {contacts.map(c => (
                <li
                  key={c.contact_id}
                  className="flex items-start justify-between gap-3 p-3 bg-[#F9F6F0] rounded-lg"
                  data-testid={`emergency-contact-${c.contact_id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-[#1A1A1A]">{c.name}</p>
                      {c.is_primary && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                          <Star className="h-3 w-3 fill-amber-500 text-amber-500" />Primary
                        </span>
                      )}
                      {c.relationship && (
                        <span className="text-[10px] text-stone-500 bg-white border border-[#E6E4DD] px-1.5 py-0.5 rounded-full">{c.relationship}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-stone-500 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>
                      {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!c.is_primary && (
                      <button
                        type="button"
                        className="text-xs text-stone-500 hover:text-[#D4AF37] px-2 py-1"
                        onClick={() => handleMakePrimary(c.contact_id)}
                        data-testid={`make-primary-${c.contact_id}`}
                      >
                        Make primary
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(c.contact_id)}
                      className="text-stone-400 hover:text-red-500 p-1"
                      data-testid={`delete-contact-${c.contact_id}`}
                      aria-label="Remove contact"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {adding && (
            <div className="mt-4 p-4 border border-[#E6E4DD] rounded-lg space-y-3 bg-white" data-testid="add-contact-form">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-[#1A1A1A]">New emergency contact</p>
                <button
                  type="button"
                  onClick={() => { setAdding(false); setForm({ name: '', phone: '', email: '', relationship: '', is_primary: false }); }}
                  className="text-stone-400 hover:text-stone-600"
                  data-testid="cancel-add-contact"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-stone-500 block mb-1">Full name</label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Mum"
                    className="input-zuriya"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-stone-500 block mb-1">Phone</label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+44 7700 900222"
                    className="input-zuriya"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-stone-500 block mb-1">Email (optional)</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="mum@example.com"
                    className="input-zuriya"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-stone-500 block mb-1">Relationship</label>
                  <Input
                    value={form.relationship}
                    onChange={(e) => setForm(f => ({ ...f, relationship: e.target.value }))}
                    placeholder="e.g. Sister, Partner"
                    className="input-zuriya"
                    data-testid="contact-relationship-input"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_primary}
                  onChange={(e) => setForm(f => ({ ...f, is_primary: e.target.checked }))}
                  className="rounded border-stone-300"
                  data-testid="contact-primary-checkbox"
                />
                Make this my primary contact (called first)
              </label>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setAdding(false); setForm({ name: '', phone: '', email: '', relationship: '', is_primary: false }); }}
                  className="border-stone-200"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAdd}
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-contact-btn"
                >
                  {saving ? 'Saving&hellip;' : 'Save contact'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
